import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, Compass, ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { ARTICLES } from "@/data/journal";

export default function Article() {
  const { slug } = useParams();
  const a = ARTICLES.find((x) => x.slug === slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!a) return <Navigate to="/journal" replace />;
  const related = ARTICLES.filter((x) => x.slug !== slug && x.category === a.category).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <article className="max-w-3xl mx-auto px-6 md:px-12 pt-32 pb-20">
        <Link to="/journal" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B] mb-8"><ArrowLeft size={14} /> The Journal</Link>
        <div className="flex items-center gap-3 text-xs mb-4">
          <span className="uppercase tracking-[0.2em] font-semibold text-[#AD6A48]">{a.category}</span>
          <span className="text-[#5C665A]">{a.date}</span>
          <span className="text-[#5C665A] inline-flex items-center gap-1"><Clock size={11} /> {a.readTime}</span>
        </div>
        <h1 data-testid="article-title" className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.08]">{a.title}</h1>
        <p className="mt-5 text-lg text-[#5C665A] leading-relaxed italic font-serif-display">{a.excerpt}</p>
        <div className="mt-8 rounded-3xl overflow-hidden">
          <img src={a.image} alt={a.title} className="w-full aspect-[16/9] object-cover" />
        </div>
        <div className="mt-10 space-y-9">
          {a.body.map((s) => (
            <section key={s.h}>
              <h2 className="font-serif-display text-2xl sm:text-3xl mb-3">{s.h}</h2>
              <p className="text-[#5C665A] leading-relaxed text-[1.05rem]">{s.p}</p>
            </section>
          ))}
        </div>
        <div className="mt-12 bg-[#2D372B] rounded-3xl p-8 text-center">
          <h3 className="font-serif-display text-2xl text-[#F7F5F0]">Turn this into a journey</h3>
          <p className="text-sm text-[#C9CFC0] mt-2">The AI planner builds a personalized itinerary around exactly this kind of experience.</p>
          <Link to="/journey" data-testid="article-plan-cta" className="mt-5 inline-flex items-center gap-2 rounded-full px-7 py-3 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
            <Compass size={15} /> Plan my journey
          </Link>
        </div>
        {related.length > 0 && (
          <div className="mt-14">
            <div className="overline mb-5">Keep Reading</div>
            <div className="grid sm:grid-cols-2 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/journal/${r.slug}`} className="group bg-white border border-[#E5E2D9] rounded-3xl p-5 hover:border-[#89937C] transition-colors">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#AD6A48]">{r.category}</span>
                  <h4 className="font-serif-display text-lg mt-2 leading-snug">{r.title}</h4>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm text-[#89937C]">Read <ArrowUpRight size={13} /></span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}
